import React, { createContext, useState } from 'react';
import { apiCall } from "../service/api";
import { useAuth } from "./AuthContext"
import { useToast } from "./ToastContext";
const AppContext = createContext();


const defaultUserData = {
    user: {
        first_name: '',
        last_name: '',
        username: '',
        email: '',
        organization: '',
    },
    billing: [], // Default empty list for billing
}
const AppProvider = ({ children }) => {

    const { token } = useAuth();
    const toast = useToast();
    const [state, setState] = useState({});
    const [profile, setProfile] = useState(defaultUserData);
    const [applications, setApplications] = useState([]);
    const [raMappings, setRaMappings] = useState([]);
    const [raProfiles, setRaProfiles] = useState([]);
    const [app, setApp] = useState({});
    const [raMapping, setRaMapping] = useState({});
    const [raProfile, setRaProfile] = useState({});
    const [bri, setBRI] = useState({});
    const [consumptions, setConsumptions] = useState([]);
    const [bris, setBRIS] = useState([]); // State for BRIS data

    // Default value for userExtended
    const [userExtended, setUserExtended] = useState(defaultUserData);

    const createUser = async (userData) => {
        const route = '/user';
        const method = 'POST';
        const data = {
            name: userData.name,
            country: userData.country,
            taxNumber: userData.taxNumber,
            username: userData.username,
            password: userData.password,
        };

        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                toast.showCorrect('User created successfully');
                return response;
            }
        } catch (err) {
            toast.showError('Error creating user');
            console.error(err);
        }
    }

    const getUsers = async (pageNumber = 1, pageSize = 5, search = '') => {
        const route = `/users?pageNumber=${pageNumber}&pageSize=${pageSize}&search=${search}`;
        const method = 'GET';
        const data = null;

        const normalizeUsersPayload = (raw, fallbackPage = pageNumber, fallbackSize = pageSize) => {
            const payload = raw?.data || raw?.result || raw;
            const items =
                payload?.items ||
                payload?.records ||
                payload?.results ||
                payload?.data ||
                [];

            const totalItems = Number(
                payload?.totalItems ||
                payload?.total_records ||
                payload?.total ||
                (Array.isArray(items) ? items.length : 0)
            );

            const normalizedPageSize = Number(payload?.pageSize || payload?.page_size || fallbackSize);
            const totalPagesFromPayload = Number(payload?.totalPages || payload?.pages || 0);
            const computedTotalPages = normalizedPageSize > 0
                ? Math.max(1, Math.ceil(totalItems / normalizedPageSize))
                : 1;

            return {
                items: Array.isArray(items) ? items : [],
                pageNumber: Number(payload?.pageNumber || payload?.page || fallbackPage),
                totalPages: totalPagesFromPayload > 0 ? totalPagesFromPayload : computedTotalPages,
            };
        };

        try {
            const response = await apiCall(route, method, data, token);
            return normalizeUsersPayload(response);
        } catch (err) {
            const status = err?.response?.status;

            if (status === 404 || status === 405) {
                try {
                    const fallbackRoute = `/bris?page=${pageNumber}&page_size=${pageSize}`;
                    const fallbackResponse = await apiCall(fallbackRoute, method, data, token);
                    return normalizeUsersPayload(fallbackResponse);
                } catch (fallbackErr) {
                    console.error(fallbackErr);
                }
            }

            console.error(err);
            return { items: [], pageNumber, totalPages: 0 };
        }
    }

    const getUser = async (id) => {
        const route = `/user/${id}`;
        const method = 'GET';
        const data = null;

        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const updatePassword = async (id, password) => {
        // http://151.80.28.87:1234/user/333310e9-7a61-41c2-91ac-b94f221b8d4c/update-password
        const route = `/user/${id}/update-password`;
        const method = 'PUT';
        const data = { password };

        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const updateUser_PasswordRoleOrganization = async (id, password, role, org_manager) => {
        const route = `/user/${id}/`;
        const method = 'PUT';
        const data = { password, role, org_manager };

        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }


    const getUserExtended = async (id) => {
        const route = `/user/${id}/extended`;
        const method = 'GET';
        const data = null;
        setUserExtended(defaultUserData);
        try {
            const response = await apiCall(route, method, data, token);
            setUserExtended(response);
        } catch (err) {
            console.error(err);
        }
    }

    const getProfile = async () => {
        const route = '/profile';
        const method = 'GET';
        const data = null;
        setProfile(defaultUserData);
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                setProfile(response);
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const getApplications = async () => {
        const route = '/applications';
        const method = 'GET';
        const data = null;
        setApplications([]);
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                setApplications(response);
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const getApp = async (id) => {
        const route = `/application/${id}`;
        const method = 'GET';
        const data = null;
        setApp({});
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                setApp(response);
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const getRAMappings = async (page = 1, pageSize = 10, filters = {}) => {
        const queryParams = new URLSearchParams();
        queryParams.append('page', String(page));
        queryParams.append('page_size', String(pageSize));

        if (filters?.ra_name) {
            queryParams.append('ra_name', filters.ra_name);
        }

        if (filters?.bri_id) {
            queryParams.append('bri_id', filters.bri_id);
        }

        const route = `/ra?${queryParams.toString()}`;
        const method = 'GET';
        const data = null;
        setRaMappings({});
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                const payload = response?.data || response?.result || response;
                const records =
                    payload?.records ||
                    payload?.items ||
                    payload?.results ||
                    payload?.data ||
                    [];
                const normalized = {
                    page: Number(payload?.page || page),
                    page_size: Number(payload?.page_size || pageSize),
                    total_records: Number(payload?.total_records || payload?.total || records.length || 0),
                    records: Array.isArray(records) ? records : [],
                };

                setRaMappings(normalized);
                return normalized;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const getRAMapping = async (id) => {
        const route = `/ra/${id}`;
        const method = 'GET';
        const data = null;
        setRaMapping({});
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                setRaMapping(response);
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const getRAProfiles = async (page = 1, pageSize = 10, filters = {}) => {
        const queryParams = new URLSearchParams();
        queryParams.append('page', String(page));
        queryParams.append('page_size', String(pageSize));

        if (filters?.profile) {
            queryParams.append('profile', filters.profile);
        }

        if (filters?.profile_category) {
            queryParams.append('profile_category', filters.profile_category);
        }

        if (filters?.country) {
            queryParams.append('country', filters.country);
        }

        const route = `/ra-profiles?${queryParams.toString()}`;
        const method = 'GET';
        const data = null;
        setRaProfiles({});
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                const payload = response?.data || response?.result || response;
                const records =
                    payload?.records ||
                    payload?.items ||
                    payload?.results ||
                    payload?.data ||
                    [];

                const normalized = {
                    page: Number(payload?.page || page),
                    page_size: Number(payload?.page_size || pageSize),
                    total_records: Number(payload?.total_records || payload?.total || records.length || 0),
                    records: Array.isArray(records) ? records : [],
                };

                setRaProfiles(normalized);
                return normalized;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const getRAProfile = async (id) => {
        const route = `/ra-profiles/${id}`;
        const method = 'GET';
        const data = null;
        setRaProfile({});
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                setRaProfile(response);
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const postApplication = async (applicationData) => {
        const method = 'POST';
        const data = {
            id: applicationData.id,
            name: applicationData.name,
            description: applicationData.description,
            OnePlatformMetric_id: applicationData.OnePlatformMetric_id,
            UanatacaBillingCode: applicationData.UanatacaBillingCode || applicationData.code,
        };

        try {
            return await apiCall('/application', method, data, token);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404 || status === 405) {
                return await apiCall('/applications', method, data, token);
            }

            console.error(err);
            throw err;
        }
    }

    const putApplication = async (applicationId, applicationData) => {

        const method = 'PUT';
        const data = {
            id: applicationId,
            name: applicationData.name,
            description: applicationData.description,
            OnePlatformMetric_id: applicationData.OnePlatformMetric_id,
            UanatacaBillingCode: applicationData.UanatacaBillingCode || applicationData.code,
        };

        try {
            return await apiCall(`/application/${applicationId}`, method, data, token);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404 || status === 405) {
                try {
                    return await apiCall('/application/', method, data, token);
                } catch (fallbackErr) {
                    const fallbackStatus = fallbackErr?.response?.status;
                    if (fallbackStatus === 404 || fallbackStatus === 405) {
                        return await apiCall('/application', method, data, token);
                    }
                    throw fallbackErr;
                }
            }

            console.error(err);
            throw err;
        }
    }

    const postRAMapping = async (raMappingData) => {
        const method = 'POST';
        const data = {
            ra_name: raMappingData.ra_name,
            bri_id: raMappingData.bri_id,
        };

        try {
            return await apiCall('/ra', method, data, token);
        } catch (err) {
            console.error(err);
            throw err;
        }
    }

    const putRAMapping = async (raMappingId, raMappingData) => {
        const method = 'PUT';
        const data = {
            ra_name: raMappingData.ra_name,
            bri_id: raMappingData.bri_id,
        };

        try {
            return await apiCall(`/ra/${raMappingId}`, method, data, token);
        } catch (err) {
            console.error(err);
            throw err;
        }
    }

    const deleteRAMapping = async (raMappingId) => {
        const method = 'DELETE';
        try {
            return await apiCall(`/ra/${raMappingId}`, method, null, token);
        } catch (err) {
            console.error(err);
            throw err;
        }
    }

    const postRAProfile = async (raProfileData) => {
        const method = 'POST';
        const data = {
            profile: raProfileData.profile,
            profile_category: raProfileData.profile_category,
            country: raProfileData.country,
            pki_name: raProfileData.pki_name,
            description: raProfileData.description,
        };

        try {
            return await apiCall('/ra-profiles', method, data, token);
        } catch (err) {
            console.error(err);
            throw err;
        }
    }

    const putRAProfile = async (raProfileId, raProfileData) => {
        const method = 'PUT';
        const data = {
            profile: raProfileData.profile,
            profile_category: raProfileData.profile_category,
            country: raProfileData.country,
            pki_name: raProfileData.pki_name,
            description: raProfileData.description,
        };

        try {
            return await apiCall(`/ra-profiles/${raProfileId}`, method, data, token);
        } catch (err) {
            console.error(err);
            throw err;
        }
    }

    const deleteRAProfile = async (raProfileId) => {
        const method = 'DELETE';
        try {
            return await apiCall(`/ra-profiles/${raProfileId}`, method, null, token);
        } catch (err) {
            console.error(err);
            throw err;
        }
    }

    const postLoadCredits = async (user, app, amount) => {
        // /user/620e98b4-125e-49c3-a885-9782012bcd12/billing-application/oneshot-signature/loadcredit
        const route = `/user/${user}/billing-application/${app}/loadcredit`;
        const method = 'POST';
        const data = { amount };
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                // toast.showCorrect('Credits loaded successfully');
                return response;
            }
        } catch (err) {
            // toast.showError('Error loading credits');
            console.error(err);
        }
    }

    const transferCredits = async (user, app, amount) => {
        const route = `/user/${user}/billing-application/${app}/transfer-credit`;
        const method = 'POST';
        const data = { amount };
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                // toast.showCorrect('Credits transferred successfully');
                return response;
            }
        } catch (err) {
            // toast.showError('Error transferring credits');
            console.error(err);
        }
    }

    const postWithdrawCredits = async (user, app, amount) => {
        debugger;
        const route = `/user/${user}/billing-application/${app}/withdraw-credit`;
        const method = 'POST';
        const data = { amount };
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const postInitializeUser = async (id, email, password) => {
        const route = `/user/${id}/initialize`;
        const method = 'POST';
        const data = { email, password };
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const postEnableUser = async (id) => {
        const route = `/user/${id}/enable`;
        const method = 'POST';
        const data = null;
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const postDisableUser = async (id) => {
        const route = `/user/${id}/disable`;
        const method = 'POST';
        const data = null;
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const getBilling = async (userId, appId) => {
        const route = `/user/${userId}/billing-application/${appId}`;
        const method = 'GET';
        const data = null;
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const getBRIS = async (pageNumber = 1, pageSize = 10, search = '') => {
        const route = `/bris?page=${pageNumber}&page_size=${pageSize}&search=${search}`;
        const method = 'GET';
        const data = null;
        try {
            const response = await apiCall(route, method, data, token);
            if (!response) {
                return { items: [], pageNumber, totalPages: 0 };
            }

            const payload = response?.data || response?.result || response;
            const items =
                payload?.items ||
                payload?.records ||
                payload?.results ||
                payload?.data ||
                [];

            const normalized = {
                items: Array.isArray(items) ? items : [],
                pageNumber: Number(payload?.pageNumber || payload?.page || pageNumber),
                totalPages: Number(payload?.totalPages || payload?.pages || 0),
            };

            setBRIS(normalized);
            return normalized;
        } catch (err) {
            console.error(err);
            return { items: [], pageNumber, totalPages: 0 };
        }
    }

    const getBRI = async (bri_id) => {
        const route = `/bri/${bri_id}`;
        const method = 'GET';
        const data = null;
        try {
            const response = await apiCall(route, method, data, token);
            if (response) {
                setBRI(response);
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    const getConsumption = async (
        customer = null,
        product = null,
        date_from = null,
        date_to = null,
        page = 1,
        page_size = 10
    ) => {
        const queryParams = new URLSearchParams();

        if (customer) queryParams.append('customer', customer);
        if (product) queryParams.append('product', product);
        if (date_from) queryParams.append('date_from', date_from);
        if (date_to) queryParams.append('date_to', date_to);
        queryParams.append('page', String(page));
        queryParams.append('page_size', String(page_size));

        const method = 'GET';
        const data = null;
        const queryString = queryParams.toString();

        try {
            const response = await apiCall(`/consumption?${queryString}`, method, data, token);
            if (response) {
                setConsumptions(response.records || response.items || response.data || response.results || []);
                return response;
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404 || status === 405) {
                return await apiCall(`/consumptions?${queryString}`, method, data, token);
            }
            console.error(err);
        }
    }




    const value = {
        state,
        profile,
        userExtended,
        applications,
        raMappings,
        raProfiles,
        app,
        raMapping,
        raProfile,
        bri,
        consumptions,
        createUser,
        setState,
        getUsers,
        getUser,
        updatePassword,
        getUserExtended,
        getProfile,
        getApplications,
        getApp,
        getRAMappings,
        getRAMapping,
        getRAProfiles,
        getRAProfile,
        postApplication,
        putApplication,
        postRAMapping,
        putRAMapping,
        deleteRAMapping,
        postRAProfile,
        putRAProfile,
        deleteRAProfile,
        postLoadCredits,
        transferCredits,
        postInitializeUser,
        postEnableUser,
        postDisableUser,
        getBilling,
        updateUser_PasswordRoleOrganization,
        postWithdrawCredits,
        getBRI,
        getBRIS,
        getConsumption
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
const useAppContext = () => {
    const context = React.useContext(AppContext);
    if (!context) {
        throw new Error("useAppContext must be used within an AppProvider");
    }
    return context;
};

export { AppContext, AppProvider, useAppContext };